import { Rating, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import ApprovalStatus from "components/Molecules/Products/approvalStatus";
import OrderStatus from "components/Molecules/Products/orderStatus";
import ToggleStatus from "components/Molecules/Products/toggleStatus";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { GetScrollCategoriesRequest, GetScrollSubcategoriesRequest } from "services/modules/lookups/categories";
import { getScrollProductsOptionTypesRequest } from "services/modules/lookups/productOptionTypes";
import { GetScrollOccasionTypesRequest } from "services/modules/occasions/occasionsTypes";
import {
  createProductRequest,
  getProductDetailsRequest,
  getProductsRequest,
  updateProductRequest,
} from "services/modules/products";
import { StoreUser } from "services/modules/utils/constant";
import setAuthToken from "services/modules/utils/handel_api";
import { GetScrollVendorsRequest } from "services/modules/vendors";
import ProductsTemplate from "templates/products/productsListing";
import EditProductPage from "./editProduct";
import "./products.css";
import { ClearProductDetailsSuccess } from "services/modules/products/Action";

function ProductsPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  setAuthToken(token);

  const userType = localStorage.getItem("user_type");
  const isStore = userType == StoreUser;

  let [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const type = searchParams.get("type");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const currentUser = useSelector(
    (state) => state.authenticate.currentUserProfile,
  );
  const permissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.products,
  );
  const variantPermissions = useSelector(
    (state) => state.authenticate.currentUserProfile.permissions.variants,
  );
  const isSuperAdmin = currentUser?.privilege == "super_admin";

  const list = useSelector((state) => state.products.products);
  const count = useSelector((state) => state.products.count);
  const details = useSelector((state) => state.products.product);
  const load = useSelector((state) => state.products.load);

  const occassions = useSelector(
    (state) => state.occasionsTypes.filterOccation,
  );
  const occassionsCount = useSelector(
    (state) => state.occasionsTypes.listCount,
  );
  const occassionsLoading = useSelector((state) => state.occasionsTypes.load);

  const vendors = useSelector((state) => state.vendors.filterVendors);
  const vendorsCount = useSelector((state) => state.vendors.listCount);
  const vendorsLoading = useSelector((state) => state.vendors.load);

  const optionTypes = useSelector(
    (state) => state.productsOptionTypes.filterProductsOptionTypes,
  );
  const optionTypesCount = useSelector(
    (state) => state.productsOptionTypes.listCount,
  );
  const optionTypesLoading = useSelector(
    (state) => state.productsOptionTypes.load,
  );

  const categories = useSelector((state) => state.categories.filterCategories);
  const categoriesCount = useSelector((state) => state.categories.listCount);
  const categoriesLoading = useSelector((state) => state.categories.load);
  const subcategories = useSelector(
    (state) => state.categories.filterSubcategories,
  );
  const subcategoriesCount = useSelector(
    (state) => state.categories.subcategoriesCount,
  );

  const fetchVendors = (filterObj) => {
    if (isStore) return;
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        hide_details: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollVendorsRequest(requestData));
  };

  const fetchOccassions = (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        hide_details: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollOccasionTypesRequest(requestData));
  };

  const fetchOptionTypes = (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        hide_details: true,
        ...filterObj,
      },
    };
    dispatch(getScrollProductsOptionTypesRequest(requestData));
  };

  const fetchCategories = (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        hide_details: true,
        ...filterObj,
      },
    };
    dispatch(GetScrollCategoriesRequest(requestData));
  };

  const fetchSubcategories = (filterObj = {}) => {
    dispatch(
      GetScrollSubcategoriesRequest({
        params: {
          page_size: 10,
          page_number: 1,
          hide_details: true,
          ...filterObj,
        },
      }),
    );
  };

  const prepareVendorsOptions = () => {
    if (isStore) {
      //to display only the logged in store in the vendors DDL when adding a product
      return [{ id: currentUser?.id, name: currentUser?.name }];
    } else {
      return vendors;
    }
  };

  const options = {
    vendors: {
      options: prepareVendorsOptions(),
      onOpen: fetchVendors,
      isLoading: vendorsLoading?.loading,
      getList: fetchVendors,
      count: vendorsCount,
    },
    occassions: {
      options: occassions,
      onOpen: occassions?.length <= 1 && fetchOccassions,
      isLoading: occassionsLoading?.loading,
      getList: fetchOccassions,
      count: occassionsCount,
    },
    optionTypes: {
      options: optionTypes,
      onOpen: optionTypes?.length <= 1 && fetchOptionTypes,
      isLoading: optionTypesLoading?.loading,
      getList: fetchOptionTypes,
      count: optionTypesCount,
    },
    categories: {
      options: categories,
      onOpen: categories?.length <= 1 && fetchCategories,
      isLoading: categoriesLoading?.loading,
      getList: fetchCategories,
      count: categoriesCount,
    },
    subcategories: {
      options: subcategories,
      onOpen: subcategories?.length <= 1 && fetchSubcategories,
      isLoading:
        categoriesLoading?.loading &&
        categoriesLoading?.type === "subcategories-list",
      getList: fetchSubcategories,
      count: subcategoriesCount,
    },
    onCategoriesChange: (value) => {
      const parentIds = (value || [])
        .map((item) => item?.value)
        .filter(Boolean);
      fetchSubcategories({ parent_ids: parentIds });
    },
  };

  const handlePageChange = (page) => {
    setPage(page);
    setFilterObj((prev) => {
      return { ...prev, page_number: page };
    });
  };

  const getDetails = async () => {
    dispatch(getProductDetailsRequest({ id }));
  };

  const getList = async (filterObj) => {
    const requestData = {
      params: {
        page_size: 10,
        page_number: 1,
        keyword: "",
        sort_by: "id",
        ...filterObj,
      },
    };
    dispatch(getProductsRequest(requestData));
  };

  const mappedData = list?.map((data, index) => {
    return {
      ...data,
      nameEN: <Stack width="150px">{data?.localized_data.en.name}</Stack>,
      nameAR: <Stack width="150px">{data?.localized_data.ar.name}</Stack>,
      vendor: <Stack width="150px">{data?.store}</Stack>,
      occasion: <Stack width="200px">{data?.occasion_types}</Stack>,
      category: <Stack width="150px">{data?.category_names}</Stack>,
      price: (
        <Stack width="130px">
          {data?.price} {data?.currency?.name}
        </Stack>
      ),
      price_after_discount: (
        <Stack width="200px">
          {data?.price_after_discount} {data?.currency?.name}
        </Stack>
      ),
      rate: (
        <Stack direction="row" alignItems="center" width="250px">
          <Rating
            name="read-only"
            value={data?.avg_rate}
            readOnly
            precision={0.5}
          />
          <Typography>{data?.avg_rate}</Typography>
          <Typography>({data?.reviews_count})</Typography>
        </Stack>
      ),
      display_priority: data?.display_priority ?? "-",
      is_featured: data?.is_featured ? t("common.yes") : t("common.no"),
      featured_product_priority: data?.featured_product_priority ?? "-",
      featured_in_store: data?.featured_in_store ? t("common.yes") : t("common.no"),
      featured_store_priority: data?.featured_store_priority ?? "-",
      featured_in_categories: data?.featured_in_categories ? t("common.yes") : t("common.no"),
      featured_category_priority: data?.featured_category_priority ?? "-",
      featured_in_occasions: data?.featured_in_occasions ? t("common.yes") : t("common.no"),
      featured_occasion_priority: data?.featured_occasion_priority ?? "-",
      status: (
        <Stack width="120px" justifyContent="start" direction="row">
          <OrderStatus data={data} />
        </Stack>
      ),
      toggle_status: (
        <Stack width="120px" justifyContent="start" direction="row">
          <ToggleStatus data={data} refetch={() => getList(filterObj)} />
        </Stack>
      ),
      approval_status: (
        <Stack width="120px" justifyContent="start" direction="row">
          <ApprovalStatus data={data} />
        </Stack>
      ),
    };
  });

  const rowData = (data) => {
    setSelectedRow(data);
  };

  const breadcrumbs = [
    {
      active: true,
      path: "/",
      title: t("vendors.home"),
    },
    {
      active: !type ? false : true,
      path: "/products",
      title: t("products.breadcrumb_products"),
    },
    type && {
      active: false,
      title:
        type === "details"
          ? t("products.breadcrumb_product_details")
          : id
            ? t("products.breadcrumb_edit_product")
            : t("products.breadcrumb_add_product"),
    },
  ];
  const prepareAddProductData = (data) => {
    const genders = {
      1: "male",
      2: "female",
      3: "any",
    };

    const master_attributes = {
      media_attributes: data?.images
        ?.map((url, i) => {
          return {
            media_type: "photo",
            url,
            file_name: `image ${i}`,
          };
        })
        .filter((img) => img?.url),
    };
    const option_type_ids = data?.option_types?.map((option) => option?.value);
    const occasion_type_ids = data?.occasion_types?.map(
      (option) => option?.value,
    );
    const category_ids = [
      ...(data?.categories?.map((option) => option?.value) || []),
      ...(data?.subcategories?.map((option) => option?.value) || []),
    ];
    const tag_ids = data?.tag_ids?.map((option) => option?.value);

    return {
      ...data,
      master_attributes,
      option_type_ids,
      occasion_type_ids,
      category_ids,
      tag_ids,
      target_gender: genders[data?.target_gender],
      thumbnail: data?.thumbnail,
      display_priority:
        data?.display_priority === "" ||
        data?.display_priority === null ||
        data?.display_priority === undefined
          ? ""
          : Number(data?.display_priority),
    };
  };

  const handelSubmit = (data, formik) => {
    const addProductData = {
      ...prepareAddProductData(data),
    };

    const action = (data) => {
      searchParams.delete("id");
      searchParams.delete("type");
      navigate(`/products/${data?.data?.product?.id}?type=edit`);
      !id && formik?.resetForm();
      //try to clear details here
      dispatch(ClearProductDetailsSuccess());
      toast.success(t("products.success_added_proceed_variant"));
    };

    const requestData = {
      body: {
        product: {
          ...addProductData,
        },
      },
      action,
    };

    dispatch(createProductRequest(requestData));
  };

  const apporval_statuses = {
    Approved: "available",
    Pending: "unverified",
    Deleted: "deleted",
    Rejected: "rejected",
  };

  const filterList = [
    !isStore && {
      placeholder: t("products.col_vendor"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, store_id: value?.value, page_number: 1 };
        });
      },
      options: vendors && vendors,
      getList: fetchVendors,
      applySearchFromRequest: true,
      count: vendorsCount,
      isLoading: vendorsLoading?.loading,
      onMenuOpen: () => {
        vendors?.length <= 1 && fetchVendors({});
      },
    },
    {
      type: "filter",
      placeholder: t("products.label_occasions"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, occasion_id: value?.value, page_number: 1 };
        });
      },
      onMenuOpen: () => {
        occassions?.length == 0 && fetchOccassions({});
      },
      options: occassions && occassions,
      getList: fetchOccassions,
      applySearchFromRequest: true,
      count: occassionsCount,
      isLoading: occassionsLoading?.loading,
    },
    {
      type: "filter",
      placeholder: t("products.col_status"),
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, status: value?.value, page_number: 1 };
        });
      },
      options: [
        { id: true, name: t("vendors.status_active") },
        { id: false, name: t("vendors.status_disabled") },
      ],
    },
    {
      type: "filter",
      placeholder: t("products.col_approval_status"),
      onChange: (value) => {
        setFilterObj((s) => {
          return {
            ...s,
            approval_status: apporval_statuses[value?.label],
            page_number: 1,
          };
        });
      },
      options: [
        { id: 1, name: t("vendors.status_approved") },
        { id: 2, name: t("vendors.status_deleted") },
        { id: 3, name: t("vendors.status_rejected") },
        { id: 4, name: t("vendors.status_pending") },
      ],
    },
    {
      type: "filter",
      placeholder: t("products.label_vouchers"), // I need to add this key or use a generic one
      onChange: (value) => {
        setFilterObj((s) => {
          return { ...s, is_voucher: value?.value, page_number: 1 };
        });
      },
      options: [
        { id: true, name: t("vendors.yes") }, // I need to add these or find existing
        { id: false, name: t("vendors.no") },
      ],
    },
    {
      type: "filter",
      placeholder: t("products.label_featured_filter") || "Featured Filter",
      onChange: (value) => {
        setFilterObj((s) => {
          const newFilterObj = { ...s, page_number: 1 };
          delete newFilterObj.featured_product;
          delete newFilterObj.featured_store;
          delete newFilterObj.featured_category;
          delete newFilterObj.featured_occasion;

          if (value?.id === "product") {
            newFilterObj.featured_product = true;
          } else if (value?.id === "store") {
            newFilterObj.featured_store = true;
          } else if (value?.id === "category") {
            newFilterObj.featured_category = true;
          } else if (value?.id === "occasion") {
            newFilterObj.featured_occasion = true;
          } else if (value?.id === "both") {
            newFilterObj.featured_product = true;
            newFilterObj.featured_store = true;
          }
          return newFilterObj;
        });
      },
      options: [
        { id: "product", name: t("products.featured_product") || "Featured Product" },
        { id: "store", name: t("products.featured_store") || "Featured Store" },
        { id: "category", name: t("products.featured_in_categories") || "Featured Category" },
        { id: "occasion", name: t("products.featured_in_occasions") || "Featured Occasion" },
        { id: "both", name: t("products.featured_both") || "Both (Product & Store)" },
      ],
    },
    {
      type: "filter",
      placeholder: t("products.label_sort_by") || "Sort Priority",
      onChange: (value) => {
        setFilterObj((s) => {
          const newFilterObj = { ...s, page_number: 1 };
          if (value) {
            newFilterObj.sort_by = value?.id;
          } else {
            delete newFilterObj.sort_by;
          }
          return newFilterObj;
        });
      },
      options: [
        { id: "featured_product_priority", name: t("products.sort_featured_product_priority") || "Sort by Featured Product Priority" },
        { id: "featured_store_priority", name: t("products.sort_featured_store_priority") || "Sort by Featured Store Priority" },
        { id: "featured_category_priority", name: t("products.sort_featured_category_priority") || "Sort by Featured Category Priority" },
        { id: "featured_occasion_priority", name: t("products.sort_featured_occasion_priority") || "Sort by Featured Occasion Priority" },
      ],
    },
    {
      type: "date",
      placeholder: "",

      onChange: (value) => {
        //if no value
        if (value != "01-01-1970") {
          setFilterObj((s) => {
            return { ...s, date: value };
          });
        } else {
          setFilterObj((s) => {
            return { ...s, date: "" };
          });
        }
      },
      sx: {
        backgroundColor: "white",
      },
    },
  ].filter((item) => item);

  const handleDelete = () => {
    const id = selectedRow?.rowData?.id;
    const action = () => {
      toast.success(t("products.success_deleted_product"));
    };
    const requestData = {
      body: {
        status: "deleted",
      },
      id,
      action,
    };
    dispatch(updateProductRequest(requestData));
    setOpenDeleteModal(false);
  };

  const handleProductApprove = (id) => {
    const action = () => {
      toast.success(t("products.success_approved"));
    };
    const requestData = {
      body: {
        status: "available",
      },
      id,
      action,
    };
    dispatch(updateProductRequest(requestData));
  };

  const handleProductReject = (id) => {
    const action = () => {
      toast.success(t("products.success_rejected"));
    };
    const requestData = {
      body: {
        status: "rejected",
      },
      id,
      action,
    };
    dispatch(updateProductRequest(requestData));
  };

  const actionsList = ({ index, data }) => {
    const status =
      data?.approval_status?.props?.children?.props?.data?.approval_status;
    return [
      {
        condition: () => {
          return true;
        },
        click: () => {
          navigate(`?type=details&id=${data?.id}`);
        },
        text: t("products.action_view_product"),
      },
      {
        condition: () => {
          return (
            status == "available" &&
            (variantPermissions?.can_read || isSuperAdmin)
          );
        },
        click: () => {
          navigate(`?type=details&id=${data?.id}&tab=1`);
        },
        text: t("products.action_view_variants"),
      },
      {
        condition: () => {
          return (
            (status == "available" ||
              status == "unverified" ||
              status == "rejected") &&
            (permissions?.can_update || isSuperAdmin)
          );
        },
        click: () => {
          navigate(`/products/${data?.id}?type=edit`);
        },
        text: t("products.action_edit"),
      },
      {
        condition: () => {
          return (
            (status == "unverified" || status == "rejected") &&
            (permissions?.can_update || isSuperAdmin) &&
            !isStore
          );
        },
        click: () => {
          handleProductApprove(data?.id);
        },
        text: t("products.action_approve"),
      },
      {
        condition: () => {
          return (
            (status == "unverified" || status == "available") &&
            (permissions?.can_update || isSuperAdmin) &&
            !isStore
          );
        },
        click: () => {
          handleProductReject(data?.id);
        },
        text: t("products.action_reject"),
      },
      {
        condition: () => {
          return (
            (status == "available" || status == "rejected") &&
            (permissions?.can_delete || isSuperAdmin)
          );
        },
        click: () => {
          setOpenDeleteModal(true);
        },
        text: t("products.action_delete"),
      },
    ];
  };

  const headerChildren = <></>;

  const modalComponents = [];

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  useEffect(() => {
    if (id) getDetails();
  }, [id]);

  return (
    <>
      <Loader
        open={
          (load?.loading && load?.type == "list") ||
          (load?.loading && load?.type == "create") ||
          (load?.loading && load?.type == "update")
        }
      />
      {type === "form" ? (
        <EditProductPage />
      ) : (
        <ProductsTemplate
          filterObj={filterObj}
          setFilterObj={setFilterObj}
          data={mappedData}
          count={count}
          paginationPage={page}
          handlePageChange={handlePageChange}
          rowData={rowData}
          details={details}
          breadcrumbs={breadcrumbs}
          filterList={filterList}
          handelSubmit={handelSubmit}
          actionsList={actionsList}
          headerChildren={headerChildren}
          openDeleteModal={openDeleteModal}
          setOpenDeleteModal={setOpenDeleteModal}
          handleDeleteRequest={handleDelete}
          modalComponents={modalComponents}
          can_create={isSuperAdmin || permissions?.can_create}
          isStore={isStore}
          options={options}
        />
      )}
    </>
  );
}

export default ProductsPage;
